import React, { useEffect, useState } from 'react';
import { questionsApi } from '@/services/questions';
import { Button } from '@/components/ui/button';

/**
 * Modal para importar questões do banco ENEM aprovadas para um Desafio ao Vivo.
 *
 * Props:
 *   open           {boolean}            - controla visibilidade
 *   onClose        {function}           - chamado ao fechar sem importar
 *   onConfirm      {function}           - chamado com array de ids selecionados
 *   areaTaxonomyId {string|number|null} - id da taxonomia de área (ENEM). Opcional.
 */
export default function ImportQuestionsModal({ open, onClose, onConfirm, areaTaxonomyId = null }) {
  const [questions, setQuestions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;

    setSelected([]);
    setQuestions([]);
    setError(null);

    let cancelled = false;

    async function fetch() {
      setLoading(true);
      try {
        const filters = { ineditas: false };
        if (areaTaxonomyId) filters['taxonomies.id'] = areaTaxonomyId;
        const res = await questionsApi.index({ filters, page: 1, perPage: 30 });
        if (!cancelled) {
          setQuestions(res?.data || []);
        }
      } catch {
        if (!cancelled) setError('Erro ao carregar questões. Tente novamente.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetch();

    return () => {
      cancelled = true;
    };
  }, [open, areaTaxonomyId]);

  function toggleId(id) {
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Importar questões do banco"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 24,
          width: '100%',
          maxWidth: 640,
          maxHeight: '80vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 16
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Importar questões do banco</h2>

        {loading && <p>Carregando questões...</p>}

        {error && (
          <p
            role="alert"
            style={{ color: '#b91c1c' }}
          >
            {error}
          </p>
        )}

        {!loading && !error && questions.length === 0 && <p>Nenhuma questão aprovada encontrada.</p>}

        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {questions.map(q => {
            const id = q.id;
            const checkboxId = `import-q-${id}`;
            return (
              <li
                key={id}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}
              >
                <input
                  type="checkbox"
                  id={checkboxId}
                  checked={selected.includes(id)}
                  onChange={() => toggleId(id)}
                  aria-label={q.description}
                />
                <label
                  htmlFor={checkboxId}
                  style={{ cursor: 'pointer', fontSize: 14, lineHeight: 1.5 }}
                >
                  {q.description}
                </label>
              </li>
            );
          })}
        </ul>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="default"
            size="md"
            onClick={() => onConfirm(selected)}
            disabled={selected.length === 0}
          >
            Importar {selected.length > 0 ? `(${selected.length})` : ''}
          </Button>
        </div>
      </div>
    </div>
  );
}
